import { abilityModifier, rollD20 } from './dice';
import { applyExhaustionHpCap } from './damage';
import { exhaustionLevel } from './conditions';
import type { RandomSource } from './random';
import type { BattleState, Combatant, Monster, PlayerCharacter } from './types';
import { emptyModifiers } from './types';

export const emptyBattleState = (): BattleState => ({
  participants: [],
  currentIndex: 0,
  round: 1,
});

export function toCombatant(
  entity: Monster | PlayerCharacter,
  createId: () => string = () => crypto.randomUUID(),
): Combatant {
  const isPc = 'hpMax' in entity;
  const hpMax = isPc ? entity.hpMax : entity.hp.average;
  return {
    uid: createId(),
    baseId: entity.id,
    kind: isPc ? 'pc' : 'monster',
    name: entity.name,
    avatar: entity.avatar,
    ac: entity.ac,
    baseMaxHp: hpMax,
    hpMax,
    hpCurrent: isPc ? entity.hpCurrent : hpMax,
    tempHp: 0,
    abilities: entity.abilities,
    resistances: entity.resistances ?? emptyModifiers(),
    vulnerabilities: entity.vulnerabilities ?? emptyModifiers(),
    immunities: entity.immunities ?? emptyModifiers(),
    actions: entity.actions.map((action) => ({ ...action, cooldown: 0 })),
    statuses: [],
    initiative: null,
  };
}

export function addCombatant(state: BattleState, incoming: Combatant): BattleState {
  const currentUid = state.participants[state.currentIndex]?.uid;
  const names = new Set(state.participants.map((participant) => participant.name));
  const baseName = incoming.name.replace(/\s+#\d+$/, '');
  let name = incoming.name;
  if (names.has(name)) {
    let suffix = 1;
    while (names.has(`${baseName} #${suffix}`)) suffix += 1;
    name = `${baseName} #${suffix}`;
  }
  const participants = [...state.participants, { ...incoming, name }];
  if (incoming.initiative !== null) {
    participants.sort(
      (a, b) =>
        (b.initiative ?? -Infinity) - (a.initiative ?? -Infinity) ||
        (b.initiativeModifier ?? 0) - (a.initiativeModifier ?? 0),
    );
  }
  return {
    ...state,
    participants,
    currentIndex: currentUid
      ? Math.max(
          0,
          participants.findIndex((item) => item.uid === currentUid),
        )
      : 0,
  };
}

export function prepareCombatantForEntry(
  state: BattleState,
  combatant: Combatant,
  random: RandomSource,
): Combatant {
  const inProgress = state.participants.some((participant) => participant.initiative !== null);
  if (!inProgress) return combatant;
  const initiativeRoll = rollD20('normal', random).kept;
  const initiativeModifier = abilityModifier(combatant.abilities.dex);
  return {
    ...combatant,
    initiativeRoll,
    initiativeModifier,
    initiative: initiativeRoll + initiativeModifier,
    justJoined: true,
  };
}

export function rollInitiative(state: BattleState, random: RandomSource): BattleState {
  const currentUid = state.participants[state.currentIndex]?.uid;
  const participants = state.participants
    .map((participant) => {
      const roll = rollD20('normal', random).kept;
      const modifier = abilityModifier(participant.abilities.dex);
      return {
        ...participant,
        initiativeRoll: roll,
        initiativeModifier: modifier,
        initiative: roll + modifier,
      };
    })
    .sort(
      (a, b) =>
        (b.initiative ?? 0) - (a.initiative ?? 0) ||
        (b.initiativeModifier ?? 0) - (a.initiativeModifier ?? 0),
    );
  const currentIndex = currentUid
    ? Math.max(
        0,
        participants.findIndex((p) => p.uid === currentUid),
      )
    : 0;
  return { ...state, participants, currentIndex };
}

function tickCombatant(combatant: Combatant): Combatant {
  const statuses = combatant.statuses.flatMap((status) => {
    const rounds = Math.max(0, Math.floor(status.rounds) - 1);
    if (status.key !== 'exhaustion') return rounds > 0 ? [{ ...status, rounds }] : [];
    const level = Math.min(6, Math.max(1, Math.floor(status.level ?? 1)));
    const stepRounds = Math.max(
      1,
      Math.floor(status.stepRounds ?? Math.max(1, status.rounds)),
    );
    if (rounds > 0) return [{ ...status, level, rounds, stepRounds }];
    if (level <= 1) return [];
    return [{ ...status, level: level - 1, rounds: stepRounds, stepRounds }];
  });
  const actions = combatant.actions.map((action) => ({
    ...action,
    cooldown: Math.max(0, action.cooldown - 1),
  }));
  return applyExhaustionHpCap(
    { ...combatant, statuses, actions },
    exhaustionLevel({ ...combatant, statuses }),
  );
}

export function nextTurn(state: BattleState): BattleState {
  if (!state.participants.length) return state;
  let participants = [...state.participants];
  const endingIndex = Math.min(state.currentIndex, participants.length - 1);
  const ending = participants[endingIndex];
  if (ending) participants[endingIndex] = tickCombatant(ending);
  const removeDefeatedMonster = ending?.kind === 'monster' && ending.hpCurrent <= 0;
  if (removeDefeatedMonster) participants = participants.filter((item) => item.uid !== ending.uid);
  if (!participants.length) return { participants: [], currentIndex: 0, round: state.round };
  let currentIndex = removeDefeatedMonster
    ? Math.min(endingIndex, participants.length)
    : endingIndex + 1;
  let round = state.round;
  let checks = 0;
  while (checks <= participants.length) {
    if (currentIndex >= participants.length) {
      currentIndex = 0;
      round += 1;
      participants = participants.map((participant) => ({ ...participant, justJoined: undefined }));
    }
    if (!participants[currentIndex]?.justJoined) break;
    currentIndex += 1;
    checks += 1;
  }
  return {
    participants,
    currentIndex: Math.min(currentIndex, participants.length - 1),
    round,
  };
}

export function previousTurn(state: BattleState): BattleState {
  if (!state.participants.length) return state;
  const wraps = state.currentIndex <= 0;
  return {
    ...state,
    currentIndex: wraps ? state.participants.length - 1 : state.currentIndex - 1,
    round: wraps ? Math.max(1, state.round - 1) : state.round,
  };
}

export function reorderInitiative(state: BattleState, from: number, to: number): BattleState {
  if (from === to || !state.participants[from] || !state.participants[to]) return state;
  const currentUid = state.participants[state.currentIndex]?.uid;
  const participants = [...state.participants];
  const [moved] = participants.splice(from, 1);
  if (!moved) return state;
  participants.splice(to, 0, moved);
  return {
    ...state,
    participants,
    currentIndex: currentUid ? participants.findIndex((p) => p.uid === currentUid) : 0,
  };
}
